
import Vue from "vue";
import { formatDateTime } from "@/shared/utils/common_utils";
import Comment, { CommentType } from "@/features/Comments/Comment";
import CommentRequestModel, { ScoreCommentRequestModel, IncidentCommentRequestModel } from "@/features/Comments/CommentRequestModel";
import { useUserStore } from "@/plugins/pinia";
import { mapStores } from "pinia";
import AppError from "@/shared/components/AppError.vue";
import FileUpload from "@/features/Documents/FileUpload.vue";
import FileList from "@/features/Documents/FileList.vue";

export default Vue.extend({
    name: "CommentSection",
    components: {
        AppError,
        FileUpload,
        FileList,
    },
    data: () => ({
        showForm: false,
        commentText: "",
        comments: [] as Comment[],
        documentMetadataIDs: [] as string[],
        loading: false,
        errorMessage: "",
        showError: false,
    }),
    props: {
        commentType: {
            type: Number as () => CommentType,
            required: true,
        },
        parentID: {
            type: String,
            required: false,
            default: "",
        },
    },
    mounted() {
        if (this.parentID) {
            this.fetchComments(this.parentID);
        }
    },
    watch: {
        // When parentID changes, fetch comments
        parentID(newValue: string): void {
            if (newValue && newValue != this.parentID) {
                this.fetchComments(newValue);
            }
        },
    },
    methods: {
        cancelComment(): void {
            this.showForm = false;
            this.commentText = "";
        },
        async fetchComments(parentID: string): Promise<void> {
            try {
                this.loading = true;
                const commentData = await this.$commentService.getCommentsFromServer(this.commentType, parentID);
                this.comments = commentData.sort((a, b) => new Date(b.createdTimestampUTC).getTime() - new Date(a.createdTimestampUTC).getTime());
            } catch (err) {
                this.errorMessage = "Failed to fetch scorecard (comments)";
                this.showError = true;
            } finally {
                this.loading = false;
            }
        },
        async saveComment(): Promise<void> {
            const newComment = new CommentRequestModel();
            newComment.body = this.commentText;
            newComment.documentMetadataIDs = this.documentMetadataIDs;
            newComment.commentType = this.commentType;

            switch (this.commentType) {
                case CommentType.ScoreComment:
                    (newComment as ScoreCommentRequestModel).scoreID = this.parentID;
                    break;
                case CommentType.IncidentComment:
                    (newComment as IncidentCommentRequestModel).incidentID = this.parentID;
                    break;
                default:
                    break;
            }

            try {
                const savedComment = await this.$commentService.createComment(newComment);
                this.comments.unshift(savedComment);
                this.$emit("commentAdded");

                // reset form
                this.showForm = false;
                this.commentText = "";
                this.documentMetadataIDs = [];
            } catch (err) {
                this.errorMessage = "Failed to add comment";
                this.showError = true;
            }
        },
        formatDateForDisplay(dateString: string): string {
            const date = new Date(dateString);
            return formatDateTime(date);
        },
        documentUploaded(documentMetadataID: string): void {
            this.documentMetadataIDs.push(documentMetadataID);
        },
        documentRemoved(documentMetadataID: string): void {
            this.documentMetadataIDs = this.documentMetadataIDs.filter(id => id != documentMetadataID);
        },
    },
    computed: {
        ...mapStores(useUserStore),
    },
});
